
import React, { useState } from 'react';
import { MessageSquare, X, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

interface AICopilotProps {
  onClose: () => void;
}

interface Message {
  id: number;
  content: string;
  isUser: boolean;
}

const SAMPLE_RESPONSES = [
  "I've analyzed your ALM position. Based on your current cash flows, I recommend considering short-term investments in T-bills to optimize your liquidity buffer.",
  "Looking at your debt portfolio, you have three loans with interest rates above market average. Would you like me to suggest refinancing options?",
  "Based on your compliance calendar, you have 2 regulatory filings due in the next 7 days. Would you like me to prepare the documentation?",
  "I notice your investment portfolio has a duration mismatch with your liability profile. Consider rebalancing to reduce interest rate risk.",
  "Your secretarial compliance requirements for the quarter have been completed successfully. No pending actions required."
];

// Financial metrics data
const FINANCIAL_METRICS = {
  gnpa: {
    personal_loans: "1.8%",
    business_loans: "2.3%",
    auto_loans: "1.2%",
    home_loans: "0.9%",
    overall: "1.6%"
  },
  roa: {
    personal_loans: "2.1%",
    business_loans: "2.7%",
    auto_loans: "1.9%",
    home_loans: "1.5%",
    overall: "2.2%"
  },
  month: "April 2025"
};

const AICopilot: React.FC<AICopilotProps> = ({ onClose }) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      content: "Hello! I'm your financial AI assistant. How can I help you today?",
      isUser: false
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSend = () => {
    if (!input.trim()) return;

    // Add user message
    const newUserMessage: Message = {
      id: messages.length + 1,
      content: input,
      isUser: true
    };
    
    setMessages([...messages, newUserMessage]);
    setInput('');
    setIsLoading(true);

    // Generate response based on user input
    setTimeout(() => {
      const userQuery = newUserMessage.content.toLowerCase();
      let response = "";
      
      // Check for specific financial metric queries
      if (userQuery.includes("gnpa") || 
          userQuery.includes("gross non-performing") || 
          userQuery.includes("non-performing assets")) {
        if (userQuery.includes("product") || userQuery.includes("wise")) {
          response = `Here are the latest product-wise GNPA numbers as of ${FINANCIAL_METRICS.month}:\n\n` +
                     `Personal Loans: ${FINANCIAL_METRICS.gnpa.personal_loans}\n` +
                     `Business Loans: ${FINANCIAL_METRICS.gnpa.business_loans}\n` +
                     `Auto Loans: ${FINANCIAL_METRICS.gnpa.auto_loans}\n` +
                     `Home Loans: ${FINANCIAL_METRICS.gnpa.home_loans}\n\n` +
                     `Overall GNPA: ${FINANCIAL_METRICS.gnpa.overall}`;
        } else {
          response = `The overall GNPA as of ${FINANCIAL_METRICS.month} is ${FINANCIAL_METRICS.gnpa.overall}.`;
        }
      } else if (userQuery.includes("roa") || userQuery.includes("return on assets")) {
        if (userQuery.includes("product") || userQuery.includes("wise")) {
          response = `Here are the latest product-wise Return on Assets (RoA) figures as of ${FINANCIAL_METRICS.month}:\n\n` +
                     `Personal Loans: ${FINANCIAL_METRICS.roa.personal_loans}\n` +
                     `Business Loans: ${FINANCIAL_METRICS.roa.business_loans}\n` +
                     `Auto Loans: ${FINANCIAL_METRICS.roa.auto_loans}\n` +
                     `Home Loans: ${FINANCIAL_METRICS.roa.home_loans}\n\n` +
                     `Overall RoA: ${FINANCIAL_METRICS.roa.overall}`;
        } else {
          response = `The overall Return on Assets (RoA) as of ${FINANCIAL_METRICS.month} is ${FINANCIAL_METRICS.roa.overall}.`;
        }
      } else if (userQuery.includes("gnpa") && userQuery.includes("roa")) {
        response = `As of ${FINANCIAL_METRICS.month}:\n\n` +
                   `Product-wise GNPA:\n` +
                   `Personal Loans: ${FINANCIAL_METRICS.gnpa.personal_loans}\n` +
                   `Business Loans: ${FINANCIAL_METRICS.gnpa.business_loans}\n` +
                   `Auto Loans: ${FINANCIAL_METRICS.gnpa.auto_loans}\n` +
                   `Home Loans: ${FINANCIAL_METRICS.gnpa.home_loans}\n\n` +
                   `Product-wise RoA:\n` +
                   `Personal Loans: ${FINANCIAL_METRICS.roa.personal_loans}\n` +
                   `Business Loans: ${FINANCIAL_METRICS.roa.business_loans}\n` +
                   `Auto Loans: ${FINANCIAL_METRICS.roa.auto_loans}\n` +
                   `Home Loans: ${FINANCIAL_METRICS.roa.home_loans}\n\n` +
                   `Overall: GNPA ${FINANCIAL_METRICS.gnpa.overall}, RoA ${FINANCIAL_METRICS.roa.overall}`;
      } else {
        // Default to random response for other queries
        response = SAMPLE_RESPONSES[Math.floor(Math.random() * SAMPLE_RESPONSES.length)];
      }
      
      const newAIMessage: Message = {
        id: messages.length + 2,
        content: response,
        isUser: false
      };
      
      setMessages(prev => [...prev, newAIMessage]);
      setIsLoading(false);
    }, 1500);
  };

  return (
    <div className="fixed right-6 bottom-6 w-96 bg-card border shadow-lg rounded-lg overflow-hidden flex flex-col z-50" style={{ height: '500px' }}>
      {/* Header */}
      <div className="p-4 border-b flex items-center justify-between bg-muted">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-rench-green" />
          <h3 className="font-semibold">AI Financial Copilot</h3>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      
      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {messages.map((message) => (
            <div 
              key={message.id} 
              className={cn(
                "flex max-w-[80%] rounded-lg p-3",
                message.isUser ? 
                  "bg-primary text-primary-foreground ml-auto" : 
                  "bg-muted text-muted-foreground"
              )}
            >
              {message.content.split('\n').map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i < message.content.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </div>
          ))}
          {isLoading && (
            <div className="flex items-center gap-1 text-muted-foreground p-2 max-w-[80%] bg-muted rounded-lg">
              <div className="w-2 h-2 rounded-full bg-muted-foreground/70 animate-bounce"></div>
              <div className="w-2 h-2 rounded-full bg-muted-foreground/70 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-2 h-2 rounded-full bg-muted-foreground/70 animate-bounce" style={{ animationDelay: '0.4s' }}></div>
            </div>
          )}
        </div>
      </ScrollArea>
      
      {/* Input */}
      <div className="p-4 border-t">
        <div className="flex items-center gap-2">
          <Input 
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about finance, compliance, or reports..."
            className="flex-1"
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          />
          <Button 
            size="icon" 
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AICopilot;
